import React, { useEffect, useRef, useState } from "react";
import { SortByOption } from "./TableFilters";

interface SortByDropdownProps {
  value?: SortByOption | null;
  onChange: (sortBy: SortByOption | null) => void;
  className?: string;
}

const SORT_BY_OPTIONS: { value: SortByOption; label: string }[] = [
  { value: "newest", label: "Newest" },
  { value: "oldest", label: "Oldest" },
];

const SortByDropdown: React.FC<SortByDropdownProps> = ({
  value,
  onChange,
  className = "ms-2",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const handleSelect = (sortBy: SortByOption | null) => {
    setIsOpen(false);
    onChange(sortBy);
  };

  return (
    <div className={`position-relative ${className}`} ref={dropdownRef}>
      <button
        type="button"
        className={`btn ${
          isOpen ? "btn-primary" : "btn-outline-light"
        } shadow d-inline-flex align-items-center position-relative`}
        aria-expanded={isOpen}
        onClick={(e) => {
          if (isOpen) e.currentTarget.blur();
          setIsOpen((open) => !open);
        }}
      >
        <i className="ti ti-sort-ascending-2 me-2" />
        Sort By
        <i className={`ti ti-chevron-${isOpen ? "up" : "down"} ms-2`} />
        {value && (
          <span className="position-absolute top-0 start-100 translate-middle p-1 bg-danger border border-light rounded-circle">
            <span className="visually-hidden">Sort active</span>
          </span>
        )}
      </button>
      {isOpen && (
        <div
          className="dropdown-menu show"
          style={{
            position: "absolute",
            top: "100%",
            right: 0,
            left: "auto",
            marginTop: "0.125rem",
            minWidth: "160px",
            zIndex: 9999,
          }}
        >
          {SORT_BY_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              className={`dropdown-item ${
                value === option.value ? "text-primary fw-medium" : ""
              }`}
              onClick={() => handleSelect(option.value)}
            >
              {option.label}
            </button>
          ))}
          {value && (
            <>
              <div className="dropdown-divider" />
              <button
                type="button"
                className="dropdown-item text-danger"
                onClick={() => handleSelect(null)}
              >
                <i className="ti ti-x me-1" />
                Clear
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default SortByDropdown;
